import isValid from 'date-fns/is_valid'
import { PhoneNumberUtil } from 'google-libphonenumber'

import { getCountryDataBasedOnDialCode } from '@flowwai-frontend/shared/utils/common'
import {
   FieldValidationReturnType,
   PhoneNumberResponse
} from '../types/Field/field'
import { FileUploaderFieldResponseType } from '../types/Field/uploaderFields'
import { RelationFieldResponseType } from '../types/Field/relationField'
import {
   GeoLocationFieldResponseType,
   GeoShapeFieldResponseType
} from '../types/Field/geoLocationField'
import { ShortTextFieldResponseType } from '../types/Field/shortTextField'

const requiredFieldErrorMsg = (): string => 'Required'
const inValidFieldErrorMsg = (): string => 'Invalid'

export const validFieldResponse: FieldValidationReturnType = {
   isValid: true
}

export const requiredFieldResponse = (): FieldValidationReturnType => ({
   isValid: false,
   errorMessage: requiredFieldErrorMsg()
})

export const inValidFieldResponse = (): FieldValidationReturnType => ({
   isValid: false,
   errorMessage: inValidFieldErrorMsg()
})

export const STRINGIFIED_FIELD_VALIDATION_REGEX = {
   phoneNumber: '^\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$',
   email: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
   url:
      '(http(s)?:\\/\\/.)?(www\\.)?[-a-zA-Z0-9@:%._~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_.~#?&//=]*)',
   number: '^\\s*[+-]?(\\d+|\\.\\d+|\\d+\\.\\d+|\\d+\\.)(e[+-]?\\d+)?\\s*$',
   float: '^-?\\d*(\\.\\d+)?$'
}

export const validateFieldWithRegEx = (
   fieldResponse: string | null | undefined,
   regEx: string
): FieldValidationReturnType => {
   const regexValue = new RegExp(regEx)

   if (!fieldResponse) return requiredFieldResponse()

   if (fieldResponse.match(regexValue)) return validFieldResponse

   return inValidFieldResponse()
}

export const validateMultiValueFieldWithRegex = (
   fieldResponse: string[] | null | undefined,
   regEx: string
): FieldValidationReturnType => {
   if (!fieldResponse) return requiredFieldResponse()

   const result: FieldValidationReturnType[] = []

   fieldResponse.forEach(each =>
      result.push(validateFieldWithRegEx(each, regEx))
   )

   const isInvalid: boolean = result.some(each => each.isValid === false)

   return isInvalid ? inValidFieldResponse() : validFieldResponse
}

// #region Text Field Validations
export const validateTextField = (
   fieldResponse: string | null | undefined
): FieldValidationReturnType => {
   const isNotValidFieldResponse =
      fieldResponse === null ||
      fieldResponse === undefined ||
      !fieldResponse.trim()
   if (isNotValidFieldResponse) return requiredFieldResponse()

   return validFieldResponse
}

export const validateEmailField = (
   fieldResponse: string | null | undefined
): FieldValidationReturnType =>
   validateFieldWithRegEx(
      fieldResponse,
      STRINGIFIED_FIELD_VALIDATION_REGEX.email
   )

export const validateURLField = (
   fieldResponse: string | null | undefined
): FieldValidationReturnType =>
   validateFieldWithRegEx(fieldResponse, STRINGIFIED_FIELD_VALIDATION_REGEX.url)

export const validatePasswordField = (
   fieldResponse: string | null | undefined
): FieldValidationReturnType => {
   const password = fieldResponse

   if (!password) return requiredFieldResponse()

   if (password.length >= 8) return validFieldResponse

   return inValidFieldResponse()
}

export const validateUniqueIdField = (
   fieldResponse: string | null | undefined
): FieldValidationReturnType => {
   if (!fieldResponse || !fieldResponse.trim()) return requiredFieldResponse()

   if (
      fieldResponse.toUpperCase() !== fieldResponse ||
      fieldResponse.includes(' ') ||
      fieldResponse.includes('/')
   )
      return inValidFieldResponse()

   return validFieldResponse
}

export const validateFileField = (
   fieldResponse: FileUploaderFieldResponseType
): FieldValidationReturnType => {
   if (!fieldResponse || !fieldResponse.length) return requiredFieldResponse()

   return validFieldResponse
}

// #endregion

// #region  Number Fields Validations

export const validateIntegerField = (
   fieldResponse: string | null | undefined
): FieldValidationReturnType => {
   if (
      fieldResponse === null ||
      fieldResponse === undefined ||
      fieldResponse === ''
   )
      return requiredFieldResponse()

   const integerValue = parseInt(fieldResponse)

   const isValid32BitSignedInteger =
      integerValue >= -2147483648 && integerValue <= 2147483647

   if (isValid32BitSignedInteger) return validFieldResponse

   return inValidFieldResponse()
}

export const validateFloatField = (
   fieldResponse: string | null | undefined
): FieldValidationReturnType => {
   if (
      fieldResponse === null ||
      fieldResponse === undefined ||
      fieldResponse === ''
   )
      return requiredFieldResponse()

   const floatValue = parseFloat(fieldResponse)

   const isValid32BitSignedInteger =
      floatValue >= -2147483648 && floatValue <= 2147483647

   if (isValid32BitSignedInteger) return validFieldResponse

   return inValidFieldResponse()
}

// #endregion

// #region Phone Number Field Validations

export const validatePhoneNumberField = (
   fieldResponse: PhoneNumberResponse | null
): FieldValidationReturnType => {
   //FIXME: unable to use conditional encapsulation
   if (!fieldResponse || !fieldResponse.phoneNumber)
      return requiredFieldResponse()

   try {
      const { phoneNumber, dialCode } = fieldResponse

      let isValid = false
      const countries = getCountryDataBasedOnDialCode(dialCode || '')

      const phoneUtil = PhoneNumberUtil.getInstance()
      countries.forEach(eachCountry => {
         const regionCode = eachCountry.iso2.toUpperCase()

         isValid = phoneUtil.isValidNumberForRegion(
            phoneUtil.parse(phoneNumber, regionCode),
            regionCode
         )

         if (isValid) {
            return
         }
      })

      return isValid ? validFieldResponse : inValidFieldResponse()
   } catch (error) {
      return inValidFieldResponse()
   }
}

// #endregion

// #region Date Field Validations

export const validateDateField = (
   fieldResponse: Date | null | undefined
): FieldValidationReturnType => {
   if (!fieldResponse) return requiredFieldResponse()

   if (isValid(fieldResponse)) return validFieldResponse
   return inValidFieldResponse()
}

// #endregion

// #region Time Field Validations

export const validateTimeField = (
   fieldResponse: string | null | undefined
): FieldValidationReturnType => {
   const regEx = /^(?:2[0-3]|[01][0-9]):[0-5][0-9]:[0-5][0-9]$/

   if (!fieldResponse) return requiredFieldResponse()

   const isValid = regEx.test(fieldResponse)

   if (isValid) return validFieldResponse

   return inValidFieldResponse()
}

// #endregion

export const validateMultiValueField = (
   fieldResponse: Array<string> | null | undefined
): FieldValidationReturnType => {
   if (!fieldResponse || !fieldResponse.length) return requiredFieldResponse()

   return validFieldResponse
}

// #region  Custom Regex validations

export const validateFieldWithCustomRegex = (
   regEx: string,
   fieldResponse?: string | null,
   regexErrorMessage?: string | null
): FieldValidationReturnType => {
   const regExp = new RegExp(regEx)

   if (fieldResponse?.match(regExp)) return validFieldResponse

   return {
      isValid: false,
      errorMessage: regexErrorMessage || inValidFieldErrorMsg()
   }
}

// #endregion

// #region Relation Field Validations

export const validateRelationField = (
   fieldResponse: RelationFieldResponseType
): FieldValidationReturnType => {
   if (!fieldResponse || !fieldResponse.length) return requiredFieldResponse()

   return validFieldResponse
}

// #endregion

// #region  Geo Location Field Validations
export const validateCoordinates = (
   latitude: number,
   longitude: number
): boolean => {
   const isValidLatitude = validateLatitude(latitude)
   const isValidLongitude = validateLongitude(longitude)

   return isValidLatitude && isValidLongitude
}

export const validateLatitude = (latitude: number): boolean =>
   latitude >= -90 && latitude <= 90

export const validateLongitude = (longitude: number): boolean =>
   longitude >= -180 && longitude <= 180

export const validateGeoLocationField = (
   fieldResponse: GeoLocationFieldResponseType
): FieldValidationReturnType => {
   if (fieldResponse === null || fieldResponse === undefined)
      return requiredFieldResponse()

   const { latitude, longitude } = fieldResponse

   const isValidLatitude = validateLatitude(latitude)
   const isValidLongitude = validateLongitude(longitude)

   if (isValidLatitude && isValidLongitude) return validFieldResponse

   return inValidFieldResponse()
}

export const validateGeoShapeField = (
   fieldResponse: GeoShapeFieldResponseType
): FieldValidationReturnType => {
   if (fieldResponse === null || fieldResponse === undefined)
      return requiredFieldResponse()

   const isInvalidValue = fieldResponse.some(
      response => !validateCoordinates(response.latitude, response.longitude)
   )

   if (isInvalidValue) return inValidFieldResponse()

   return validFieldResponse
}
// #endregion

// #region Short Text Field Validations
export const validateShortTextField = (
   fieldResponse: ShortTextFieldResponseType | null | undefined,
   arePrefixOptionsPresent: boolean,
   areSuffixOptionsPresent: boolean
): FieldValidationReturnType => {
   const isNotValidFieldResponse =
      fieldResponse === null ||
      fieldResponse === undefined ||
      !fieldResponse.value.trim()
   if (isNotValidFieldResponse) return requiredFieldResponse()

   const isValidPrefix = arePrefixOptionsPresent ? !!fieldResponse.prefix : true
   const isValidSuffix = areSuffixOptionsPresent ? !!fieldResponse.suffix : true

   if (!isValidPrefix || !isValidSuffix) return requiredFieldResponse()

   return validFieldResponse
}
// #endregion
